// Model picker in the chat input bar: a small button showing the current
// tier, opening a menu with a hint and relative cost per tier. Balanced is
// shown in the primary colour; Thorough, the expensive one, in orange with a
// filled background and a warning strip above the input bar.
import React, { useState } from 'react';
import { Alert, Box, Button, Chip, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import BalanceIcon from '@mui/icons-material/Balance';
import BoltIcon from '@mui/icons-material/Bolt';
import CheckIcon from '@mui/icons-material/Check';
import PsychologyIcon from '@mui/icons-material/Psychology';

import { CHAT_MODEL_OPTIONS, DEFAULT_CHAT_MODEL_TIER, THOROUGH_WARNING, chatModelOption, answerModelLabel } from './chatModels';
import type { ChatModelTier } from './chatModels';

const TIER_ICONS: Record<ChatModelTier, React.ReactElement> = {
    fast: <BoltIcon fontSize="small" />,
    balanced: <BalanceIcon fontSize="small" />,
    thorough: <PsychologyIcon fontSize="small" />,
};

const ChatModelPicker: React.FC<{
    value: ChatModelTier;
    onChange: (tier: ChatModelTier) => void;
    disabled?: boolean;
}> = ({ value, onChange, disabled }) => {
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const current = chatModelOption(value) ?? CHAT_MODEL_OPTIONS[0];
    const thorough = current.tier === 'thorough';
    const pick = (tier: ChatModelTier) => {
        setAnchor(null);
        onChange(tier);
    };
    return (
        <>
            <Tooltip title={thorough ? 'Thorough is on: expensive, use sparingly' : 'Model for the next answers'}>
                <span>
                    <Button
                        size="small"
                        onClick={(e) => setAnchor(e.currentTarget)}
                        disabled={disabled}
                        startIcon={TIER_ICONS[current.tier]}
                        endIcon={<ArrowDropDownIcon />}
                        aria-haspopup="menu"
                        aria-label={`Model: ${current.label}`}
                        sx={{
                            textTransform: 'none',
                            borderRadius: '16px',
                            minWidth: 0,
                            px: 1,
                            whiteSpace: 'nowrap',
                            fontWeight: thorough ? 600 : undefined,
                            color: thorough ? 'warning.dark'
                                : current.tier === DEFAULT_CHAT_MODEL_TIER ? 'text.secondary' : 'primary.main',
                            bgcolor: thorough ? (t) => alpha(t.palette.warning.main, 0.15) : undefined,
                            '&:hover': thorough ? { bgcolor: (t) => alpha(t.palette.warning.main, 0.25) } : undefined,
                            '& .MuiButton-startIcon': { mr: { xs: 0, sm: 0.5 } },
                        }}
                    >
                        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{current.label}</Box>
                    </Button>
                </span>
            </Tooltip>
            <Menu
                anchorEl={anchor}
                open={Boolean(anchor)}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                {CHAT_MODEL_OPTIONS.map((o) => (
                    <MenuItem key={o.tier} selected={o.tier === value} onClick={() => pick(o.tier)}>
                        <ListItemIcon>{TIER_ICONS[o.tier]}</ListItemIcon>
                        <ListItemText
                            primary={o.tier === DEFAULT_CHAT_MODEL_TIER ? `${o.label} (default)` : o.label}
                            secondary={o.hint}
                        />
                        <Typography
                            variant="caption"
                            color={o.tier === 'thorough' ? 'warning.dark' : 'text.secondary'}
                            sx={{ ml: 3, minWidth: '2.5em', fontWeight: o.tier === 'thorough' ? 600 : undefined }}
                        >
                            {o.cost}
                        </Typography>
                        <Box sx={{ width: 20, ml: 1, display: 'flex' }}>
                            {o.tier === value && <CheckIcon fontSize="small" color="primary" />}
                        </Box>
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};

// Above the input bar for as long as Thorough is on; not dismissible, so it
// cannot stay on unnoticed. One click goes back to Fast.
export const ThoroughWarning: React.FC<{
    tier: ChatModelTier;
    onChange: (tier: ChatModelTier) => void;
}> = ({ tier, onChange }) => {
    if (tier !== 'thorough') return null;
    return (
        <Alert
            severity="warning"
            sx={{ mb: 1, py: 0, fontSize: '0.8rem', borderRadius: 2, alignItems: 'center' }}
            action={
                <Button color="inherit" size="small" sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                        onClick={() => onChange(DEFAULT_CHAT_MODEL_TIER)}>
                    Switch to Fast
                </Button>
            }
        >
            {THOROUGH_WARNING}
        </Alert>
    );
};

// Under an answer from Balanced or Thorough: the tier, and for admins what
// the answer cost. Default answers get nothing.
export const AnswerModelChip: React.FC<{
    tier: string | undefined;
    costUsd: number | undefined;
    model: string | undefined;
}> = ({ tier, costUsd, model }) => {
    const label = answerModelLabel(tier, costUsd);
    const option = chatModelOption(tier);
    if (!label || !option) return null;
    const tooltip = costUsd === undefined
        ? model ?? ''
        : `Cost of this answer, all model calls included (USD) · ${model ?? option.label}`;
    return (
        <Tooltip title={tooltip}>
            <Chip
                size="small"
                variant="outlined"
                color={option.tier === 'thorough' ? 'warning' : 'info'}
                icon={TIER_ICONS[option.tier]}
                label={label}
                sx={{ ml: 1, mt: 0.5, fontWeight: costUsd === undefined ? undefined : 600 }}
            />
        </Tooltip>
    );
};

export default ChatModelPicker;
